import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

interface OutcomeFormProps {
  claimId: string;
  currentStatus: string;
  onSuccess?: () => void;
}

type OutcomeType = 'submitted' | 'accepted' | 'partial' | 'rejected' | null;

export function OutcomeForm({ claimId, currentStatus, onSuccess }: OutcomeFormProps) {
  const [step, setStep] = useState<'initial' | 'submitted' | 'response'>('initial');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form fields
  const [submittedDate, setSubmittedDate] = useState('');
  const [outcome, setOutcome] = useState<OutcomeType>(null);
  const [compensationAmount, setCompensationAmount] = useState('');
  const [councilResponse, setCouncilResponse] = useState('');

  const handleMarkSubmitted = async () => {
    if (!submittedDate) {
      setError('Please enter the date you submitted the claim');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await api.post(`/api/claims/${claimId}/outcome`, {
        action: 'mark_submitted',
        submittedAt: submittedDate,
      });

      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecordOutcome = async () => {
    if (!outcome) {
      setError('Please select an outcome');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await api.post(`/api/claims/${claimId}/outcome`, {
        action: 'record_outcome',
        outcome,
        compensationAmount: compensationAmount ? parseFloat(compensationAmount) : undefined,
        councilResponse,
      });

      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  // Show different prompts based on current status
  if (currentStatus === 'pack_generated' || step === 'submitted') {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <h3 className="font-display text-lg font-semibold text-brand-950 mb-4">
          📬 Track Your Submission
        </h3>

        {step === 'initial' ? (
          <div className="space-y-4">
            <p className="text-sm text-surface-600">
              Have you submitted your claim to the council? Let Mac know so he can help you track your progress.
            </p>

            <div>
              <label className="block text-sm font-medium text-brand-950 mb-1">
                Date submitted
              </label>
              <Input
                type="date"
                value={submittedDate}
                onChange={(e) => setSubmittedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {error && (
              <p className="text-sm text-error-600">{error}</p>
            )}

            <div className="flex gap-3">
              <Button onClick={handleMarkSubmitted} loading={isLoading}>
                I've Submitted My Claim
              </Button>
              <Button variant="ghost" onClick={() => setStep('response')}>
                Already got a response
              </Button>
            </div>
          </div>
        ) : (
          <OutcomeSelection
            outcome={outcome}
            setOutcome={setOutcome}
            compensationAmount={compensationAmount}
            setCompensationAmount={setCompensationAmount}
            councilResponse={councilResponse}
            setCouncilResponse={setCouncilResponse}
            error={error}
            isLoading={isLoading}
            onSubmit={handleRecordOutcome}
            onBack={() => setStep('initial')}
          />
        )}
      </div>
    );
  }

  if (currentStatus === 'submitted' || currentStatus === 'awaiting_response') {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <h3 className="font-display text-lg font-semibold text-brand-950 mb-4">
          📩 Record Council Response
        </h3>

        <OutcomeSelection
          outcome={outcome}
          setOutcome={setOutcome}
          compensationAmount={compensationAmount}
          setCompensationAmount={setCompensationAmount}
          councilResponse={councilResponse}
          setCouncilResponse={setCouncilResponse}
          error={error}
          isLoading={isLoading}
          onSubmit={handleRecordOutcome}
        />
      </div>
    );
  }

  return null;
}

interface OutcomeSelectionProps {
  outcome: OutcomeType;
  setOutcome: (o: OutcomeType) => void;
  compensationAmount: string;
  setCompensationAmount: (v: string) => void;
  councilResponse: string;
  setCouncilResponse: (v: string) => void;
  error: string | null;
  isLoading: boolean;
  onSubmit: () => void;
  onBack?: () => void;
}

function OutcomeSelection({
  outcome,
  setOutcome,
  compensationAmount,
  setCompensationAmount,
  councilResponse,
  setCouncilResponse,
  error,
  isLoading,
  onSubmit,
  onBack,
}: OutcomeSelectionProps) {
  const outcomeOptions = [
    {
      value: 'accepted' as const,
      label: 'Claim Accepted ✅',
      description: 'Council accepted liability and will pay',
      color: 'success',
    },
    {
      value: 'partial' as const,
      label: 'Partial Payment 📊',
      description: 'Council offered partial compensation',
      color: 'warning',
    },
    {
      value: 'rejected' as const,
      label: 'Claim Rejected ❌',
      description: 'Council denied liability',
      color: 'error',
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-surface-600">
        What was the council's response to your claim?
      </p>

      <div className="grid gap-3">
        {outcomeOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setOutcome(option.value)}
            className={clsx(
              'w-full p-4 rounded-xl border-2 text-left transition-all group',
              outcome === option.value
                ? option.color === 'success'
                  ? 'border-success-500 bg-success-50 shadow-md'
                  : option.color === 'warning'
                  ? 'border-warning-500 bg-warning-50 shadow-md'
                  : 'border-error-500 bg-error-50 shadow-md'
                : 'border-surface-200 bg-surface-50 hover:border-brand-300 hover:bg-white hover:shadow-md cursor-pointer'
            )}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-brand-950">{option.label}</p>
                <p className="text-sm text-surface-600">{option.description}</p>
              </div>
              <svg 
                className={clsx(
                  "w-5 h-5 transition-transform",
                  outcome === option.value 
                    ? "text-brand-600" 
                    : "text-surface-300 group-hover:text-brand-400 group-hover:translate-x-0.5"
                )} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        ))}
      </div>

      {(outcome === 'accepted' || outcome === 'partial') && (
        <div>
          <label className="block text-sm font-medium text-brand-950 mb-1">
            Compensation amount (£)
          </label>
          <Input
            type="number"
            placeholder="e.g. 350.00"
            value={compensationAmount}
            onChange={(e) => setCompensationAmount(e.target.value)}
          />
          <p className="text-xs text-surface-500 mt-1">
            How much did the council agree to pay?
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-brand-950 mb-1">
          Notes (optional)
        </label>
        <Textarea
          placeholder="Any notes about the council's response..."
          value={councilResponse}
          onChange={(e) => setCouncilResponse(e.target.value)}
          rows={3}
        />
      </div>

      {error && (
        <p className="text-sm text-error-600">{error}</p>
      )}

      <div className="flex gap-3">
        {onBack && (
          <Button variant="ghost" onClick={onBack}>
            Back
          </Button>
        )}
        <Button onClick={onSubmit} loading={isLoading} disabled={!outcome}>
          Save Outcome
        </Button>
      </div>

      {outcome === 'rejected' && (
        <div className="mt-4 p-4 bg-brand-50 border border-brand-200 rounded-lg">
          <p className="text-sm font-medium text-brand-800">
            💡 Don't give up! You can escalate to small claims court.
          </p>
          <p className="text-xs text-brand-600 mt-1">
            Court document preparation is planned, but is not currently available to purchase.
          </p>
        </div>
      )}
    </div>
  );
}
